import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { compact } from 'lodash';
import { FormGenerateReport } from 'app/components/RoomPage/modalGenReport';
import { PayloadCreateReport, postReport } from 'services/report';
import { AssetIssues } from 'types/room';
import { getErrorMsg } from 'utils/getErrorMsg';

type FlashFn = (flash: { message: string; type: 'success' | 'info' | 'warning' | 'error' }) => void;

export const resolveReportRoomId = (
  ...candidates: Array<number | string | undefined | null>
): number | undefined => {
  for (const candidate of candidates) {
    const n = Number(candidate);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return undefined;
};

export const buildCreateReportPayload = (
  value: FormGenerateReport,
  roomLocationId?: number
): PayloadCreateReport => {
  const payload: PayloadCreateReport = {
    title: value.title,
    showAppendixFile: value.showAppendixFile,
    select: value.select
  };

  const roomId = resolveReportRoomId(roomLocationId);
  const submittedAssetIds = compact(value.assets?.map((item) => Number(item)));
  const issueReportIds = compact(value.issues?.map((item) => Number(item)));
  const roomLevelIssueIds = compact(value.roomLevelIssueIds?.map((item) => Number(item)));
  const storageIds = compact(value.storageIds?.map((item) => Number(item)));

  if (roomLevelIssueIds.length) payload.roomLevelIssueIds = roomLevelIssueIds;
  if (storageIds.length) payload.storageIds = storageIds;

  if (value.select === 'all') {
    if (roomId) {
      payload.roomId = roomId;
      payload.selectAll = true;
    }
  } else if (value.select === 'selective') {
    if (submittedAssetIds.length) payload.submittedAssetIds = submittedAssetIds;
    if (roomId) payload.roomId = roomId;
  } else if (value.select === 'selectIssues') {
    if (issueReportIds.length) payload.issueReportIds = issueReportIds;
    if (roomId) payload.roomId = roomId;
  }

  if (value.date) {
    payload.date = dayjs(value.date).startOf('day').format('YYYY-MM-DD');
  }

  return payload;
};

export const buildSingleAssetReportPayload = (asset: AssetIssues): PayloadCreateReport => ({
  title: asset.name,
  submittedAssetIds: [asset.id],
  onlyAsset: true
});

export const getReportGenerationErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const message = getErrorMsg(error);
    if (message) return message;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    const message = String((error as { message?: unknown }).message || '');
    if (message) return message;
  }
  return 'Failed to generate report';
};

const getReportSelectionError = (payload: PayloadCreateReport): string | null => {
  const hasAssets = (payload.submittedAssetIds?.length ?? 0) > 0;
  const hasIssues = (payload.issueReportIds?.length ?? 0) > 0;
  const hasRoomLevelIssues = (payload.roomLevelIssueIds?.length ?? 0) > 0;
  const hasDocs = (payload.storageIds?.length ?? 0) > 0;
  const hasAnyDropdown = hasRoomLevelIssues || hasDocs;

  if (payload.select === 'all') {
    if (payload.roomId == null) {
      return 'Select a room before generating a report';
    }
    return null;
  }

  if (payload.select === 'selective') {
    if (!hasAssets && !hasAnyDropdown) {
      return 'Please select something to generate report';
    }
    return null;
  }

  if (payload.select === 'selectIssues') {
    if (!hasIssues && !hasAnyDropdown) {
      return 'Please select something to generate report';
    }
    return null;
  }

  const hasTarget =
    payload.roomId != null ||
    hasAssets ||
    hasIssues ||
    hasRoomLevelIssues ||
    hasDocs ||
    (payload.submittedLocationIds?.length ?? 0) > 0;

  if (!hasTarget) {
    return 'Please select something to generate report';
  }

  return null;
};

export const submitReportGeneration = async (
  payload: PayloadCreateReport,
  setFlash: FlashFn
): Promise<boolean> => {
  if (!payload.title?.trim()) {
    setFlash({ type: 'error', message: 'Report title is required' });
    return false;
  }

  const selectionError = getReportSelectionError(payload);
  if (selectionError) {
    setFlash({ type: 'error', message: selectionError });
    return false;
  }

  try {
    await postReport(payload);
    setFlash({
      type: 'success',
      message: 'Report generation started. Check the Documentation tab in a few minutes.'
    });
    return true;
  } catch (error: unknown) {
    const axiosError = error as AxiosError & { code?: string };
    if (axiosError?.code === 'ECONNABORTED') {
      setFlash({
        type: 'info',
        message:
          'Your file is being generated. Please wait a few minutes and check the Documentation tab.'
      });
      return true;
    }

    const message = getReportGenerationErrorMessage(error);
    // console.error('[report generation failed]', error);
    setFlash({ type: 'error', message });
    return false;
  }
};
